import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDebounce } from 'use-debounce';
import { format } from 'date-fns';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
// import DataSheet from '@/components/Modal/Project/DataSheet';

import File from '@/api/file';

const { getDownloadLog } = File();

export default function Downloads() {
	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 300);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

	const oC = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize, order_by: '-id' };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		return obj;
	}, [search, paginationModel]);

	const {
		data: d,
		isFetching,
		isFetched,
	} = useQuery({
		queryKey: ['download-logs', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getDownloadLog(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataCount =
		useMemo(() => {
			if (isFetched && d?.data?.count) {
				oC.current = d.data.count;
				return d.count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results, [d]) || [];

	const columns = [
		{
			field: 'id',
			headerName: 'Kode',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			valueGetter: (_, row) => {
				return row?.component_data[0]?.productprefix || row.id;
			},
		},
		{
			field: 'ProductCodeNumber',
			headerName: 'Nummer',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			valueGetter: (_, row) => {
				return row?.component_data[0]?.productcodenumber || '';
			},
		},
		{
			field: 'ProductVersion',
			headerName: 'Versjon',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			valueGetter: (_, row) => {
				return row?.component_data[0]?.productversion || '';
			},
		},
		{
			field: 'ProductName',
			headerName: 'Nedlastet fil',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
			valueGetter: (_, row) => {
				return row?.component_data[0]?.productname || '';
			},
		},
		{
			field: 'UpdateBy',
			headerName: 'Bruker',
			headerAlign: 'center',
			align: 'center',
			width: 196,
			valueGetter: (_, row) => {
				return row?.user_data[0]?.email || '';
			},
		},
		{
			field: 'downloadtime',
			headerName: 'Dato',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (params) => {
				const { downloadtime } = params.row;
				return (
					<div className='flex flex-col'>
						<span>{format(new Date(downloadtime), 'yyyy/MM/dd')}</span>
					</div>
				);
			},
		},
	];

	// const onSaveDataSheet = () => {};
	return (
		<>
			<BDHelmet title='Download Log' description='BDS Samferdsel download log' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk i dokument her ...'
							onChange={(e) => {
								setSearch(e.target.value);
								if (paginationModel.page !== 0) {
									setPaginationModel({ page: 0, pageSize: 10 })
								}
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
					</div>
					<div>
						<span className='font-bold'>Totalt </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>brukere</span>
					</div>
					<div>
						<DataGrid
							getRowId={(row) => row.id}
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							// rowsPerPageOptions={[10, 20, 50]}
							className='mx-auto lg:max-w-[1600px] w-full h-full mt-0'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								if (selectedRow?.id === params.row.id) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
									pageSize: paginationModel.pageSize,
									rowCount: dataCount,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectedRow(params.row);
							}}
							initialState={{
								pagination: { paginationModel: { page: 0, pageSize: 10 } },
							}}
							rowCount={dataCount}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
						/>
					</div>
				</div>
				{/* {!!selectedRow && (
					<DataSheet open={!!selectedRow} onSave={onSaveDataSheet} data={selectedRow} onClose={() => setSelectedRow(false)} />
				)} */}
			</div>
		</>
	);
}
